import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

export interface FoodItem {
  id: string
  category: string
  description: string
  date: string
  servedDate?: string
  servings: number
  notes?: string
  assignedTo?: string
  assignedUserName?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface Event {
  eventId: string
  title: string
  description: string
  eventDate: string // Primary date field from backend
  startDate: string // Alias for eventDate
  endDate?: string
  time: string
  locationId: string
  eventUrl?: string // Optional URL for event website/info
  location?: {
    locationId: string
    name: string
    address: string
    city?: string
    state?: string
    description?: string
  }
  maxParticipants?: number // Backend uses maxParticipants instead of maxAttendees
  rsvpDeadline?: string
  createdBy: string
  createdAt: string
  updatedAt: string
  isMultiDay: boolean
  status: 'draft' | 'active' | 'completed' | 'cancelled' // Match backend status values
  rsvps?: {
    userId: string // This is the Auth0 sub
    firstName: string
    lastName: string
    joinedYear?: string
    status: 'yes' | 'no' | 'maybe'
    timestamp: string
    days?: string[]
  }[]
  scheduledEvents: {
    [date: string]: {
      id: string
      time: string
      title: string
      description: string
      location?: string
      eventUrl?: string
      coordinator?: string
      coordinatorName?: string
      isOptional?: boolean
      maxParticipants?: number
      deadline?: string
      rsvps?: string[]
    }[]
  }
  dailyFoodSlots: {
    [date: string]: {
      id: string
      category: string
      description: string
      assignedTo?: string // This is the Auth0 sub
      assignedUserName?: string
      servedDate?: string
    }[]
  }
}

export interface CreateEventData {
  title: string
  description: string
  eventDate: string
  endDate?: string
  time?: string
  locationId: string
  eventUrl?: string
  maxParticipants?: number
  rsvpDeadline?: string
  isMultiDay?: boolean
  status?: 'draft' | 'active' | 'completed' | 'cancelled'
}

export interface UpdateEventData extends Partial<CreateEventData> {}

class EventsApi {
  private getAccessTokenSilently: (() => Promise<string>) | null = null

  setAuth(getAccessTokenSilently: () => Promise<string>) {
    this.getAccessTokenSilently = getAccessTokenSilently
  }

  private async getHeaders(): Promise<HeadersInit> {
    if (!this.getAccessTokenSilently) {
      throw new Error('Auth not initialized. Call setAuth() first.')
    }
    
    try {
      const token = await this.getAccessTokenSilently()
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    } catch (error) {
      throw new Error('Authentication failed')
    }
  }

  async getEvents(): Promise<{ events: Event[]; total: number }> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events`, {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw error
    }
  }

  async getEvent(eventId: string): Promise<Event> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.event
    } catch (error) {
      throw error
    }
  }

  async createEvent(eventData: CreateEventData): Promise<Event> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(eventData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.event
    } catch (error) {
      throw error
    }
  }

  async updateEvent(eventId: string, eventData: UpdateEventData): Promise<Event> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(eventData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.event
    } catch (error) {
      throw error
    }
  }

  async deleteEvent(eventId: string): Promise<void> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      throw error
    }
  }

  async rsvpToEvent(eventId: string, userId: string, status: 'yes' | 'no' | 'maybe', days?: string[]): Promise<void> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/rsvps/${userId}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ status, days })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      throw error
    }
  }

  async claimFoodItem(eventId: string, foodId: string, notes?: string): Promise<void> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/food/${foodId}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ notes })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      throw error
    }
  }

  async updateFoodItem(eventId: string, foodId: string, updateData: Partial<FoodItem>): Promise<FoodItem> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/food/${foodId}`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.foodItem
    } catch (error) {
      throw error
    }
  }

  async deleteFoodItem(eventId: string, foodId: string): Promise<void> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/food/${foodId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      throw error
    }
  }

  async releaseFoodSlot(eventId: string, slotId: string): Promise<void> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/food/${slotId}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ action: 'release' })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      throw error
    }
  }

  async addScheduledEvent(eventId: string, date: string, eventData: any): Promise<any> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/scheduled-events`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ date, ...eventData })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      return result.scheduledEvent
    } catch (error) {
      throw error
    }
  }

  async updateScheduledEvent(eventId: string, scheduledEventId: string, eventData: any): Promise<any> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/scheduled-events/${scheduledEventId}`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(eventData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      return result.scheduledEvent
    } catch (error) {
      throw error
    }
  }

  async deleteScheduledEvent(eventId: string, scheduledEventId: string): Promise<void> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/scheduled-events/${scheduledEventId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      throw error
    }
  }

  async rsvpScheduledEvent(eventId: string, scheduledEventId: string, attending: boolean): Promise<any> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/scheduled-events/${scheduledEventId}/rsvp`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ attending })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      return result.scheduledEvent
    } catch (error) {
      throw error
    }
  }

  async addFoodItem(eventId: string, date: string, foodData: any): Promise<FoodItem> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/food`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          date,
          category: foodData.category,
          description: foodData.description,
          servedDate: foodData.servedDate,
          servings: foodData.servings || 1,
          notes: foodData.notes || '',
          assignedTo: foodData.assignedTo,
          assignedUserName: foodData.assignedUserName
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      return result.foodItem
    } catch (error) {
      throw error
    }
  }
}

export const eventsApi = new EventsApi()

// Hook to initialize eventsApi with Auth0
export const useEventsApi = () => {
  const { getAccessTokenSilently, isLoading } = useAuth0()
  
  useEffect(() => {
    if (!isLoading) {
      // Initialize the eventsApi with Auth0 token function
      eventsApi.setAuth(getAccessTokenSilently)
    }
  }, [isLoading, getAccessTokenSilently])
  
  return eventsApi
}
